import type { FormEvent, ReactNode } from "react";

interface AuthFormProps {
  title: string;
  onSubmit: (e: FormEvent) => void;
  submitLabel: string;
  isLoading?: boolean;
  error?: string;
  children: ReactNode;
  footer?: ReactNode;
}

export function AuthForm({
  title,
  onSubmit,
  submitLabel,
  isLoading = false,
  error,
  children,
  footer,
}: AuthFormProps) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-6 text-center">
          {title}
        </h1>

        <form onSubmit={onSubmit} className="space-y-4">
          {children}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-blue-600 text-white py-3 rounded-md font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? "Loading..." : submitLabel}
          </button>
        </form>

        {footer && (
          <div className="mt-6 text-center text-sm text-gray-600">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

interface InputFieldProps {
  label: string;
  type: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  autoComplete?: string;
}

export function InputField({
  label,
  type,
  value,
  onChange,
  placeholder,
  required = true,
  autoComplete,
}: InputFieldProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        autoComplete={autoComplete}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </div>
  );
}
